export const aiComputeSolutions = {
  'ai-platform': {
    title: 'AI 算力平台解决方案',
    background:
      '政企 AI 建设常从多个部门、多个地点起步，算力规格不一、数据搬运成本高，模型从训练到上线缺少统一通道；而工业和园区现场又要求低时延与数据不出域。本方案以 AI 算力平台为底座，组合算网大脑、边缘推理和安全资源，把“资源接入—模型生产—任务部署—运行反馈”串成可演进的建设路径。',
    challenges: [
      { title: '算力孤岛', desc: '多地点、多芯片资源缺乏统一目录和调度入口。' },
      { title: '数据搬运慢', desc: '训练数据在网络、存储之间反复搬运，影响任务交付节奏。' },
      { title: '模型难交付', desc: '数据、模型、镜像和部署环境分散，版本回溯困难。' },
      { title: '现场不够实时', desc: '中心集中推理存在时延，敏感数据又不宜全部上云。' },
    ],
    scenarios: [
      { title: '科研算力共享', desc: '跨院所纳管异构 GPU/NPU，按任务和链路质量分配资源。' },
      { title: '工业/园区边缘推理', desc: '中心训练、边缘部署，满足实时视觉和设备分析。' },
      { title: '混合多云 AI', desc: '接入自有云与第三方云，统一模型、镜像和知识资产视图。' },
      { title: '算网安协同', desc: '算网大脑、Overlay 网络与安全资源池按需编排。' },
    ],
    architectureCaption:
      '业务系统和开发终端接入中心算力平台；算网大脑负责跨域资源协商；模型和任务经 Overlay/SD-WAN 连接边缘节点；安全资源池为 AI 业务提供纵深防护。',
    advantages: [
      { title: '分层建设', desc: '从 IaaS 算力底座到 PaaS 运行服务，再到 MaaS 模型服务，可按阶段启用。' },
      { title: '全局调度', desc: '综合算力、存储和链路状态选择任务落点，减少盲目扩容。' },
      { title: '训推协同', desc: '训练和治理集中完成，推理能力按需下沉到边缘。' },
      { title: '数据通道优化', desc: 'RDMA、全闪和预加载等能力降低数据瓶颈（依赖硬件与许可）。' },
      { title: '安全可组合', desc: '复用云网和内生安全能力，按数据敏感度和业务等级配置。' },
    ],
    caseTitle: '某省级科研机构｜跨院所 AI 算力统一支撑',
    casePain: '多个院所分别建设 GPU 资源，利用率和排队情况不可见，模型与数据难以在合规范围内共享。',
    caseApproach:
      '建设统一算力资源视图，接入跨院所异构节点；以算网大脑辅助任务选点，集中管理模型、数据集和知识库；训练在中心完成，推理按需下沉业务节点。',
    caseResult:
      '形成统一目录和按需调度机制，减少重复建设；模型与数据可在权限和合规约束下复用，科研任务协作更加顺畅。',
  },
  'ai-native-platform': {
    title: 'AI native 平台解决方案',
    background:
      'AI 编程工具缩短了代码产出时间，却让端到端测试、回归和证据留存更容易成为瓶颈。AI native 平台解决方案以测试数字员工为切入点，把需求理解、用例生成、脚本执行、失败处理和知识沉淀连成可审计流程，在不改变研发工具链的前提下逐步提升质量保障的自动化程度。',
    challenges: [
      { title: '代码产出快、测试跟不上', desc: '版本迭代节奏加快，测试人力难以线性增加。' },
      { title: '用例脚本反复维护', desc: '端到端流程多，页面变化导致脚本易失效。' },
      { title: '执行证据分散', desc: '批量测试结果、日志和缺陷线索难统一追踪。' },
      { title: '质量经验难复用', desc: '回归规则和修复方法散落在个人与项目中。' },
    ],
    scenarios: [
      { title: '企业应用端到端回归', desc: '从文本需求生成用例，调用执行代理完成真实环境测试。' },
      { title: '关键业务流程验证', desc: '围绕核心业务流程开展页面扫描、用例生成和结果复核。' },
      { title: '交付质量门禁', desc: '把代码审核和测试结果纳入交付检查点。' },
      { title: '回归资产管理', desc: '沉淀页面路径、业务流程和验证结果等可复用知识。' },
    ],
    architectureCaption:
      '企业应用通过执行代理接入 AI native 平台，平台负责任务理解与编排，执行代理负责真实环境操作；控制与执行分工协同，便于融入现有研发流程。',
    advantages: [
      { title: '从理解到回归', desc: '需求、用例、脚本、执行和回归在一条链路上衔接。' },
      { title: '自然语言入口', desc: '业务人员可用自然语言描述目标，降低自动化使用门槛。' },
      { title: '真实环境留证', desc: '执行日志、页面证据和结果可追溯，便于复盘与审计。' },
      { title: '失败可处理', desc: '自动定位失败原因并进行修复或分类，减少重复排查。' },
      { title: '知识可沉淀', desc: '将缺陷线索和回归经验回流，支持后续任务复用。' },
    ],
    caseTitle: '某政企客户｜多云管理产品测试提质',
    casePain: '产品版本迭代频繁，端到端用例数量持续增长，测试资源难以覆盖每次发布。',
    caseApproach:
      '接入 AI native 测试数字员工，将需求文档和页面信息转换为用例，生成脚本后在真实环境批量执行；失败用例自动进入修复或分类，执行证据与缺陷线索回流。',
    caseResult:
      '重复性测试工作得到托管，测试人员可把时间投入复杂场景判断和质量策略，整体质量闭环更清晰。',
  },
}
